import * as React from 'react';
import { Card, CardTitle, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { PageLoader } from '@/components/ui/loader';
import { useQuery, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { HistoricalTradesDialog } from './HistoricalTradesDialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';

interface TradeSignal {
	_id: string;
	_creationTime: number;
	signalDate: string;
	direction: 'LONG' | 'SHORT';
	optionType: 'CALL' | 'PUT';
	strikeType?: string;
	strikePrice?: number | null;
	optionSymbol?: string | null;
	entryOptionPrice?: number | null;
	optionLastPrice?: number | null;
	expirationMonth?: string;
	expirationType?: string;
	status: 'ACTIVE' | 'CLOSED' | 'PENDING';
	isTest?: boolean;
	spyPrice?: number | null;
	spxPrice?: number | null;
}

const NewSignalForm = () => {
	const initiateSignal = useAction(api.oracle.initiateSignalCreation);
	const [direction, setDirection] = React.useState<'LONG' | 'SHORT'>('LONG');
	const [isInitiating, setIsInitiating] = React.useState(false);
	const { toast } = useToast();
	const months = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec',
	];
	// WHY: Use a single Date reference. Advance 2 months — Date constructor handles overflow
	// (e.g. Nov+2 → Jan next year). Old code used modulo which didn't advance the year.
	const now = new Date();
	const defaultExpiration = new Date(now.getFullYear(), now.getMonth() + 2, 1);
	const currentYear = now.getFullYear();
	const [month, setMonth] = React.useState(months[defaultExpiration.getMonth()]);
	const [year, setYear] = React.useState(defaultExpiration.getFullYear());

	// Generate year options: current year, next year, and year after
	const yearOptions = [currentYear, currentYear + 1, currentYear + 2];

	const handleSignalInitiation = async (isTestSignal: boolean) => {
		setIsInitiating(true);
		const signalType = isTestSignal ? 'Test Signal' : 'Signal';
		try {
			const result = await initiateSignal({
				direction,
				expirationMonthYear: `${month} ${year}`,
				isTest: isTestSignal,
			});

			if (result.success) {
				toast({
					title: `${signalType} Creation Successful`,
					description: `${direction} ${signalType.toLowerCase()} for ${month} initiated. Check logs for details.`,
					variant: 'success',
				});
			} else {
				throw new Error(
					result.error || 'Unknown error during signal initiation.'
				);
			}
		} catch (error) {
			console.error(
				`Error initiating ${signalType.toLowerCase()}:`,
				error
			);
			toast({
				title: `${signalType} Creation Failed`,
				description:
					error instanceof Error
						? error.message
						: 'An unexpected error occurred',
				variant: 'destructive',
			});
		} finally {
			setIsInitiating(false);
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		handleSignalInitiation(false);
	};

	const handleTestSignal = (e: React.MouseEvent) => {
		e.preventDefault();
		handleSignalInitiation(true);
	};

	return (
		<form onSubmit={handleSubmit} className='space-y-4'>
			<div className='flex gap-4'>
				<button
					type='button'
					onClick={() => setDirection('LONG')}
					className={cn(
						'px-4 py-2 rounded-lg font-medium',
						direction === 'LONG'
							? 'bg-green-500/20 text-green-400 border border-green-500/30'
							: 'bg-gray-800/50 text-gray-400'
					)}
					disabled={isInitiating}
				>
					LONG
				</button>
				<button
					type='button'
					onClick={() => setDirection('SHORT')}
					className={cn(
						'px-4 py-2 rounded-lg font-medium',
						direction === 'SHORT'
							? 'bg-red-500/20 text-red-400 border border-red-500/30'
							: 'bg-gray-800/50 text-gray-400'
					)}
					disabled={isInitiating}
				>
					SHORT
				</button>
				<select
					value={month}
					onChange={(e) => setMonth(e.target.value)}
					className='bg-gray-800/50 text-gray-200 rounded-lg px-4 py-2 border border-gray-700'
					disabled={isInitiating}
				>
					{months.map((m) => (
						<option key={m} value={m}>
							{m}
						</option>
					))}
				</select>
				<select
					value={year}
					onChange={(e) => setYear(Number(e.target.value))}
					className='bg-gray-800/50 text-gray-200 rounded-lg px-4 py-2 border border-gray-700'
					disabled={isInitiating}
				>
					{yearOptions.map((y) => (
						<option key={y} value={y}>
							{y}
						</option>
					))}
				</select>
				<button
					type='submit'
					className={cn(
						'px-4 py-2 bg-orange-500/20 text-orange-400 rounded-lg font-medium border border-orange-500/30',
						isInitiating
							? 'opacity-70 cursor-not-allowed'
							: 'hover:bg-orange-500/30'
					)}
					disabled={isInitiating}
				>
					{isInitiating ? 'Initiating...' : 'Add Signal'}
				</button>
				<button
					type='button'
					onClick={handleTestSignal}
					className={cn(
						'px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg font-medium border border-purple-500/30',
						isInitiating
							? 'opacity-70 cursor-not-allowed'
							: 'hover:bg-purple-500/30'
					)}
					disabled={isInitiating}
				>
					{isInitiating ? 'Initiating...' : 'Test Signal'}
				</button>
			</div>
		</form>
	);
};

const SignalCard = ({ signal }: { signal: TradeSignal }) => {
	const isLong = signal.direction === 'LONG';
	const gradientClass = isLong
		? 'from-green-500/20 to-emerald-600/20 border-green-500/30'
		: 'from-red-500/20 to-pink-600/20 border-red-500/30';

	// Add special gradient for test signals
	const isTest = signal.isTest === true;
	const testGradientClass =
		'from-purple-500/20 to-purple-600/20 border-purple-500/30';

	// Check if we have price data
	const hasPriceData =
		('spyPrice' in signal &&
			signal.spyPrice !== null &&
			signal.spyPrice !== undefined) ||
		('spxPrice' in signal &&
			signal.spxPrice !== null &&
			signal.spxPrice !== undefined);

	return (
		<div
			className={cn(
				'w-full p-6 bg-gradient-to-b rounded-lg border backdrop-blur-sm',
				isTest ? testGradientClass : gradientClass
			)}
		>
			<div className='flex justify-between items-start mb-4'>
				<div className='flex flex-col'>
					<span
						className={cn(
							'text-lg font-bold',
							isTest
								? 'text-purple-400'
								: isLong
									? 'text-green-700 dark:text-green-300'
									: 'text-red-700 dark:text-red-300'
						)}
					>
						{isTest ? '🧪 TEST SIGNAL' : 'ORACLE TRIGGER'}
					</span>
					<span className='text-2xl font-bold mt-1 text-foreground'>
						{isLong ? 'BULLISH (+) LONG' : 'BEARISH (-) SHORT'}
						{isTest && ' (TEST)'}
					</span>
				</div>
				<div className='text-right text-muted-foreground'>
					{format(new Date(signal.signalDate), 'MMM dd, yyyy')}
				</div>
			</div>

			<div className='space-y-3'>
				<div className='text-lg text-foreground'>
					Buy to Open: SPY {signal.optionType} Option
				</div>
				<div className='p-6 bg-gradient-to-b from-purple-900 to-purple-950 rounded-lg space-y-2'>
					{signal.strikePrice && signal.optionSymbol ? (
						// Display specific strike and symbol if available
						<>
							<div>
								<b className='block mb-0.5'>Strike:</b> $
								{signal.strikePrice.toFixed(2)}
							</div>
							<div>
								<b className='block mb-0.5'>Contract Symbol:</b>
								<a
									href={`https://finance.yahoo.com/quote/${signal.optionSymbol?.replace(/^O:/, '').toLowerCase()}/options`}
									target='_blank'
									rel='noopener noreferrer'
									className='text-sm bg-muted/50 px-1 py-0.5 rounded underline text-blue-400 hover:text-blue-300'
								>
									{signal.optionSymbol?.replace(/^O:/, '')}
								</a>
							</div>
							<div>
								<b className='block mb-0.5'>Expiration:</b>{' '}
								{signal.expirationMonth} (
								{signal.expirationType})
							</div>
							{typeof signal.entryOptionPrice === 'number' && (
								<div>
									<b className='block mb-0.5'>Entry Price:</b>{' '}
									${signal.entryOptionPrice.toFixed(2)} (at
									signal)
								</div>
							)}
							{typeof signal.optionLastPrice === 'number' && (
								<div>
									<b className='block mb-0.5'>Last Price:</b>{' '}
									${signal.optionLastPrice.toFixed(2)}
									{typeof signal.entryOptionPrice ===
										'number' &&
										signal.entryOptionPrice !== 0 &&
										(() => {
											const percentageChange =
												(((signal.optionLastPrice ??
													0) -
													signal.entryOptionPrice!) /
													signal.entryOptionPrice!) *
												100;
											const changeColor =
												percentageChange >= 0
													? 'text-green-400'
													: 'text-red-400';
											return (
												<span
													className={cn(
														'ml-2 font-semibold',
														changeColor
													)}
												>
													(
													{percentageChange >= 0
														? '+'
														: ''}
													{percentageChange.toFixed(
														2
													)}
													%)
												</span>
											);
										})()}
								</div>
							)}
						</>
					) : (
						// Fallback to old display if specific data isn't loaded yet
						<div className='space-y-1'>
							<div>
								Strike: {signal.strikeType}{' '}
								{signal.expirationMonth} -{' '}
								{signal.expirationType} Expiration
							</div>
						</div>
					)}
				</div>

				{hasPriceData && (
					<div className='mt-4 space-y-2 p-6 bg-gradient-to-b from-gray-800 to-gray-900 rounded-lg'>
						{'spyPrice' in signal &&
							signal.spyPrice !== null &&
							signal.spyPrice !== undefined && (
								<div className='text-lg'>
									Market Price at Signal:
									<br />
									<span className='text-muted-foreground mr-2'>
										SPY:
									</span>
									<span className='font-medium text-foreground'>
										${signal.spyPrice.toFixed(2)}
									</span>
								</div>
							)}
						{'spxPrice' in signal &&
							signal.spxPrice !== null &&
							signal.spxPrice !== undefined && (
								<div className='text-lg'>
									<span className='text-muted-foreground mr-2'>
										S&P 500:
									</span>
									<span className='font-medium text-foreground'>
										${signal.spxPrice.toFixed(2)}
									</span>
								</div>
							)}
					</div>
				)}

				<div className='mt-4 pt-4 border-t border-border'>
					<div className='text-yellow-600 dark:text-yellow-300 font-medium'>
						{isTest ? 'TEST ONLY - DO NOT TRADE' : 'REMINDER:'}
					</div>
					<div className='text-muted-foreground'>
						{!isTest &&
							`Sell to Close Previous ${isLong ? 'SHORT' : 'LONG'} Position`}
						{isTest &&
							'This is a test signal and should not be traded on.'}
					</div>
				</div>
			</div>
		</div>
	);
};

interface PriceResult {
	symbol: string;
	price: number;
	change: number;
	changesPercentage: number;
}

const LivePrices = () => {
	const marketOverviewData = useQuery(
		api.scheduledJobs.getLatestMarketOverview
	);
	const currentYearPerformance = useQuery(
		api.oracle.getCurrentYearPerformance
	);

	const isLoading = marketOverviewData === undefined;
	const hasError = marketOverviewData === null && !isLoading;

	const lastUpdated = marketOverviewData?.lastUpdatedAt
		? new Date(marketOverviewData.lastUpdatedAt)
		: null;

	const renderPrice = (
		data: PriceResult | null | undefined,
		label: string
	) => {
		if (!data) {
			return (
				<div className='text-center'>
					<div className='text-sm text-muted-foreground mb-1'>
						{label}
					</div>
					<div className='text-4xl font-bold text-muted-foreground'>
						- - -
					</div>
					<div className='text-sm text-muted-foreground'>
						Data unavailable
					</div>
				</div>
			);
		}
		const isPositive = data.change >= 0;
		return (
			<div className='text-center'>
				<div className='text-sm text-muted-foreground mb-1'>
					{label}
				</div>
				<div className='text-4xl font-bold text-foreground'>
					$
					{data.price.toLocaleString('en-US', {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					})}
				</div>
				<div
					className={cn(
						'text-sm font-medium',
						isPositive ? 'text-green-400' : 'text-red-400'
					)}
				>
					{isPositive ? '+' : ''}
					{data.change.toLocaleString('en-US', {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					})}{' '}
					({isPositive ? '+' : ''}
					{data.changesPercentage.toFixed(2)}%)
				</div>
			</div>
		);
	};

	const renderPerformance = () => {
		if (!currentYearPerformance) {
			return (
				<div className='text-center'>
					<div className='text-sm text-muted-foreground mb-1'>
						Oracle Performance {new Date().getFullYear()}
					</div>
					<div className='text-4xl font-bold text-muted-foreground'>
						- - -
					</div>
					<div className='text-sm text-muted-foreground'>
						Loading...
					</div>
				</div>
			);
		}

		const isPositive = currentYearPerformance.totalReturn >= 0;

		return (
			<div className='text-center'>
				<div className='text-sm text-muted-foreground mb-1'>
					Oracle Performance {currentYearPerformance.year}
				</div>
				<div className='text-4xl font-bold text-foreground'>
					{isPositive ? '+' : ''}
					{currentYearPerformance.totalReturn.toFixed(2)}%
				</div>
				<div className='space-y-1'>
					<div
						className={cn(
							'text-sm font-medium',
							isPositive ? 'text-green-400' : 'text-red-400'
						)}
					>
						{currentYearPerformance.tradesWithData > 0 ? (
							<>
								{currentYearPerformance.winningTrades}/
								{currentYearPerformance.tradesWithData} trades (
								{currentYearPerformance.winRate.toFixed(1)}% win
								rate)
							</>
						) : (
							<>
								{currentYearPerformance.totalSignals} total
								signals
								{currentYearPerformance.totalSignals > 0 &&
									' (no price data)'}
							</>
						)}
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className='relative mb-8 p-6 rounded-lg border border-border bg-card/50 backdrop-blur-sm'>
			{lastUpdated && (
				<div className='absolute top-4 left-4 text-xs text-muted-foreground'>
					{lastUpdated.toLocaleString('en-US', {
						year: 'numeric',
						month: 'short',
						day: 'numeric',
						hour: 'numeric',
						minute: '2-digit',
					})}
				</div>
			)}

			{isLoading && (
				<div className='text-center text-muted-foreground py-10'>
					Loading market overview...
				</div>
			)}
			{hasError && (
				<div className='text-center text-red-500 py-10'>
					Error loading market overview data. Please try again later
					or contact support.
				</div>
			)}
			{!isLoading && !hasError && marketOverviewData && (
				<div className='grid grid-cols-1 md:grid-cols-3 gap-6 pt-4'>
					{renderPrice(
						marketOverviewData.spx
							? { ...marketOverviewData.spx, symbol: 'S&P 500' }
							: null,
						'S&P 500 Index'
					)}
					{renderPrice(
						marketOverviewData.spy
							? { ...marketOverviewData.spy, symbol: 'SPY' }
							: null,
						'SPY ETF'
					)}
					{renderPerformance()}
				</div>
			)}
			{!isLoading && !hasError && !marketOverviewData && (
				<div className='text-center text-muted-foreground py-10'>
					No market overview data available. It may be updating.
				</div>
			)}
		</div>
	);
};

export function Oracle() {
	const { user, isAdmin, isLoading: authLoading } = useAuth();
	const [isPageLoading, setIsPageLoading] = React.useState(true);
	const [showNewSignal, setShowNewSignal] = React.useState(false);
	const [currentPage, setCurrentPage] = React.useState(1);
	const [sortOrder, setSortOrder] = React.useState<'desc' | 'asc'>('desc');
	const itemsPerPage = 3;
	const signals = useQuery(api.oracle.getTradeSignals);
	const isSuperAdmin =
		user?.email === 'reverend@thewatchersociety.org' ||
		user?.email === 'matt@thetrainermethod.com';

	const { toast } = useToast();
	const triggerRefresh = useAction(
		api.scheduledJobs.triggerManualMarketOverviewRefresh
	);
	const [isRefreshingMarketData, setIsRefreshingMarketData] =
		React.useState(false);

	const handleRefreshMarketData = async () => {
		setIsRefreshingMarketData(true);
		try {
			const result = await triggerRefresh();
			if (result?.success) {
				toast({
					title: 'Market Data Refresh Initiated',
					description:
						'The latest market data is being fetched. Updates should appear shortly.',
					variant: 'success',
				});
			} else {
				toast({
					title: 'Refresh Failed',
					description:
						result?.message ||
						'Could not start market data refresh.',
					variant: 'destructive',
				});
			}
		} catch (error) {
			console.error('Error triggering market data refresh:', error);
			toast({
				title: 'Refresh Error',
				description:
					error instanceof Error
						? error.message
						: 'An unexpected error occurred.',
				variant: 'destructive',
			});
		} finally {
			setIsRefreshingMarketData(false);
		}
	};

	React.useEffect(() => {
		if (!authLoading) {
			setIsPageLoading(false);
		}
	}, [authLoading]);

	// Reset pagination when sort order changes
	React.useEffect(() => {
		setCurrentPage(1);
	}, [sortOrder]);

	if (isPageLoading) {
		return <PageLoader message='CONSULTING THE ORACLE...' />;
	}

	if (!user || !isAdmin) {
		return (
			<div className='container mx-auto p-6'>
				<Card className='mt-6'>
					<CardContent>
						<div className='flex flex-col items-center justify-center space-y-6 p-8'>
							<h2 className='text-2xl font-bold text-red-500'>
								Access Denied
							</h2>
							<p className='text-lg text-gray-400 text-center'>
								You do not have permission to access The Oracle.
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	// WHY: Always pin the newest signal regardless of sort order.
	// Spread before sorting — .sort() mutates in place and would corrupt the Convex query cache.
	const newestFirst = signals
		? [...signals].sort(
				(a: TradeSignal, b: TradeSignal) =>
					new Date(b.signalDate).getTime() - new Date(a.signalDate).getTime()
			)
		: undefined;
	const latestSignal = newestFirst?.[0];

	// Apply user-selected sort order only to the history slice
	const sortedSignals = newestFirst?.slice(1).sort(
		(a: TradeSignal, b: TradeSignal) => {
			const dateA = new Date(a.signalDate).getTime();
			const dateB = new Date(b.signalDate).getTime();
			return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
		}
	);

	const totalPages = Math.ceil((sortedSignals?.length || 0) / itemsPerPage);
	const currentItems = sortedSignals?.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage
	);

	return (
		<div className='container mx-auto p-6'>
			<Card className='mt-6'>
				<CardContent className='pt-6'>
					<LivePrices />

					<div className='space-y-2 mb-8'>
						<div className='flex justify-between items-center'>
							<CardTitle className='text-3xl font-bold text-foreground'>
								The Oracle
							</CardTitle>
							<div className='flex items-center gap-2'>
								<HistoricalTradesDialog
									trigger={
										<Button
											variant='outline'
											className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
										>
											<div className='flex items-center gap-2'>
												📊 Historical Performance
											</div>
										</Button>
									}
									initialYear={2016}
								/>
								{isSuperAdmin && (
									<Button
										variant='outline'
										className='bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30'
										onClick={handleRefreshMarketData}
										disabled={isRefreshingMarketData}
									>
										{isRefreshingMarketData
											? 'Refreshing...'
											: '🔄 Refresh Market Data'}
									</Button>
								)}
								{isSuperAdmin && (
									<button
										onClick={() =>
											setShowNewSignal(!showNewSignal)
										}
										className={cn(
											'px-4 py-2 rounded-lg font-medium transition-colors',
											showNewSignal
												? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
												: 'bg-gray-800/50 text-gray-400 border border-gray-700'
										)}
									>
										{showNewSignal ? '🚫' : '🎯'}
									</button>
								)}
							</div>
						</div>
						<p className='text-sm text-muted-foreground'>
							Market signals and trading insights • Powered by
							Prometheus AI
						</p>
					</div>

					<div className='space-y-8'>
						{isSuperAdmin && showNewSignal && (
							<div className='mb-8'>
								<NewSignalForm />
							</div>
						)}

						{latestSignal && (
							<>
								<div className='mb-8'>
									<h3 className='text-3xl font-bold text-pink-500 mb-4'>
										Latest Signal
									</h3>
									<SignalCard signal={latestSignal} />
								</div>

								<div>
									<div className='flex justify-between items-center mb-4'>
										<h3 className='text-3xl font-bold text-pink-500'>
											Signal History
										</h3>
										<button
											onClick={() =>
												setSortOrder(
													sortOrder === 'desc'
														? 'asc'
														: 'desc'
												)
											}
											className='flex items-center gap-2 px-4 py-2 bg-gray-800/50 text-gray-300 rounded-lg font-medium border border-gray-700 hover:bg-gray-700/50 transition-colors'
										>
											{sortOrder === 'desc' ? '↓' : '↑'}
											{sortOrder === 'desc'
												? 'Newest First'
												: 'Oldest First'}
										</button>
									</div>
									<div className='space-y-4'>
										{currentItems?.map(
											(signal: TradeSignal) => (
												<SignalCard
													key={signal._id}
													signal={signal}
												/>
											)
										)}
									</div>

									{totalPages > 1 && (
										<div className='flex flex-col items-center justify-center gap-4 mt-8'>
											<div className='flex items-center gap-4'>
												<button
													onClick={() =>
														setCurrentPage(1)
													}
													disabled={currentPage === 1}
													className={cn(
														'px-4 py-2 rounded-lg font-medium transition-colors',
														currentPage === 1
															? 'bg-gray-800/50 text-gray-400 cursor-not-allowed'
															: 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30'
													)}
												>
													⏮️ First
												</button>
												<button
													onClick={() =>
														setCurrentPage(
															currentPage - 1
														)
													}
													disabled={currentPage === 1}
													className={cn(
														'px-4 py-2 rounded-lg font-medium transition-colors',
														currentPage === 1
															? 'bg-gray-800/50 text-gray-400 cursor-not-allowed'
															: 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30'
													)}
												>
													← Previous
												</button>
												<div className='flex items-center gap-2'>
													{(() => {
														const pageNumbers = [];
														const maxVisiblePages = 5;
														const sidePages =
															Math.floor(
																maxVisiblePages /
																	2
															);

														let startPage =
															Math.max(
																1,
																currentPage -
																	sidePages
															);
														let endPage = Math.min(
															totalPages,
															currentPage +
																sidePages
														);

														// Adjust if we're near the beginning or end
														if (
															currentPage <=
															sidePages
														) {
															endPage = Math.min(
																totalPages,
																maxVisiblePages
															);
														}
														if (
															currentPage >
															totalPages -
																sidePages
														) {
															startPage =
																Math.max(
																	1,
																	totalPages -
																		maxVisiblePages +
																		1
																);
														}

														// Add first page and ellipsis if needed
														if (startPage > 1) {
															pageNumbers.push(
																<button
																	key={1}
																	onClick={() =>
																		setCurrentPage(
																			1
																		)
																	}
																	className='w-10 h-10 rounded-lg font-medium transition-colors bg-gray-800/50 text-gray-400 border border-gray-700 hover:bg-gray-700/50'
																>
																	1
																</button>
															);
															if (startPage > 2) {
																pageNumbers.push(
																	<span
																		key='ellipsis1'
																		className='text-gray-400 px-2'
																	>
																		...
																	</span>
																);
															}
														}

														// Add visible page range
														for (
															let i = startPage;
															i <= endPage;
															i++
														) {
															pageNumbers.push(
																<button
																	key={i}
																	onClick={() =>
																		setCurrentPage(
																			i
																		)
																	}
																	className={cn(
																		'w-10 h-10 rounded-lg font-medium transition-colors',
																		currentPage ===
																			i
																			? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
																			: 'bg-gray-800/50 text-gray-400 border border-gray-700 hover:bg-gray-700/50'
																	)}
																>
																	{i}
																</button>
															);
														}

														// Add ellipsis and last page if needed
														if (
															endPage < totalPages
														) {
															if (
																endPage <
																totalPages - 1
															) {
																pageNumbers.push(
																	<span
																		key='ellipsis2'
																		className='text-gray-400 px-2'
																	>
																		...
																	</span>
																);
															}
															pageNumbers.push(
																<button
																	key={
																		totalPages
																	}
																	onClick={() =>
																		setCurrentPage(
																			totalPages
																		)
																	}
																	className='w-10 h-10 rounded-lg font-medium transition-colors bg-gray-800/50 text-gray-400 border border-gray-700 hover:bg-gray-700/50'
																>
																	{totalPages}
																</button>
															);
														}

														return pageNumbers;
													})()}
												</div>
												<button
													onClick={() =>
														setCurrentPage(
															currentPage + 1
														)
													}
													disabled={
														currentPage ===
														totalPages
													}
													className={cn(
														'px-4 py-2 rounded-lg font-medium transition-colors',
														currentPage ===
															totalPages
															? 'bg-gray-800/50 text-gray-400 cursor-not-allowed'
															: 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30'
													)}
												>
													Next →
												</button>
												<button
													onClick={() =>
														setCurrentPage(
															totalPages
														)
													}
													disabled={
														currentPage ===
														totalPages
													}
													className={cn(
														'px-4 py-2 rounded-lg font-medium transition-colors',
														currentPage ===
															totalPages
															? 'bg-gray-800/50 text-gray-400 cursor-not-allowed'
															: 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30'
													)}
												>
													Last ⏭️
												</button>
											</div>
											<div className='text-sm text-gray-300 text-center'>
												<p>
													Showing signals{' '}
													{currentPage *
														itemsPerPage -
														itemsPerPage +
														1}{' '}
													to{' '}
													{Math.min(
														currentPage *
															itemsPerPage,
														sortedSignals?.length ||
															0
													)}{' '}
													of{' '}
													{sortedSignals?.length || 0}{' '}
													total signals
												</p>
											</div>
										</div>
									)}
								</div>
							</>
						)}

						{!signals?.length && (
							<div className='flex flex-col items-center justify-center space-y-6'>
								<p className='text-lg text-gray-300 text-center max-w-2xl'>
									No trade signals available yet.
								</p>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
